import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useCdpAuth } from "../lib/coinbase/useAuth";

/**
 * Drops every cached query the moment the signed-in identity changes (sign-in, sign-out, or a
 * different account signing in on the same tab). The query cache is app-wide and keyed without a
 * user id, so without this the previous account's `/me`, orders, listings, notifications and
 * wallet-scoped data are served to the next account — which is how a freshly signed-in buyer
 * could see "This is your own listing" for the seller's items.
 *
 * Only a settled identity counts: while the CDP SDK is initializing, or signed in but still
 * resolving the user, the identity is "unknown" and ignored, so a brief SDK flicker can't wipe the
 * cache out from under a page. Renders nothing; mount once, inside the QueryClientProvider.
 */
export function IdentityCacheGuard() {
  const { isInitialized, isSignedIn, cdpUserId } = useCdpAuth();
  const queryClient = useQueryClient();
  const settled = useRef<string | null | undefined>(undefined);

  // undefined = not known yet; null = definitely signed out; string = a specific user.
  let identity: string | null | undefined;
  if (!isInitialized) identity = undefined;
  else if (!isSignedIn) identity = null;
  else identity = cdpUserId ?? undefined;

  useEffect(() => {
    if (identity === undefined) return;
    // First settled identity only records who is here; nothing cached can be wrong yet.
    if (settled.current !== undefined && settled.current !== identity) {
      void queryClient.resetQueries();
    }
    settled.current = identity;
  }, [identity, queryClient]);

  return null;
}
